import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listSuppressions, removeSuppression } from "@/lib/admin-console.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/admin/suppressions")({
  head: () => ({
    meta: [
      { title: "Suppressions — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SuppressionsPage,
});

function SuppressionsPage() {
  const fetchList = useServerFn(listSuppressions);
  const remove = useServerFn(removeSuppression);
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "suppressions"],
    queryFn: () => fetchList(),
  });

  const mutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "suppressions"] }),
  });

  const items = (data?.suppressions ?? []).filter((s: any) =>
    q ? s.email.toLowerCase().includes(q.toLowerCase()) : true,
  );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Suppressions</h1>
          <p className="text-sm text-muted-foreground">
            Bounced, complained, or unsubscribed addresses. Emails to these are blocked.
          </p>
        </div>
        <Input
          placeholder="Search email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium">Email</th>
              <th className="p-3 font-medium">Reason</th>
              <th className="p-3 font-medium">Suppressed</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s: any) => (
              <tr key={s.id} className="border-t">
                <td className="p-3 font-medium">{s.email}</td>
                <td className="p-3">
                  <Badge variant="outline">{s.reason || "unknown"}</Badge>
                </td>
                <td className="p-3 text-muted-foreground">
                  {new Date(s.created_at).toLocaleString()}
                </td>
                <td className="p-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={mutation.isPending}
                    onClick={() => {
                      if (confirm(`Remove ${s.email} from suppression list?`)) {
                        mutation.mutate(s.id);
                      }
                    }}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-muted-foreground">
                  No suppressed addresses.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
